/**
 * Single function for all API calls. Handles auth, payload, and errors.
 *
 * @param {string} base_url - API base URL
 * @param {string|null} token - Auth token (optional for public endpoints)
 * @param {string} method - HTTP method (GET, POST, PUT, DELETE)
 * @param {string} path - Path (e.g. '/core/users/login'). May include query params.
 * @param {object|FormData|null} payload - Request body (optional). Use FormData for multipart.
 * @param {{ useBearerAuth?: boolean }} options - useBearerAuth: use Authorization Bearer instead of x-auth-token
 * @returns {Promise<object>} Parsed JSON response
 * @throws {Error} On non-OK response or network error (message from server when available)
 */
export const apiCall = async (base_url, token, method, path, payload = null, options = {}) => {
  const headers = { 'Content-Type': 'application/json' };

  if (token) {
    if (options.useBearerAuth) {
      headers['Authorization'] = `Bearer ${token}`;
    } else {
      headers['x-auth-token'] = token;
    }
  }

  const fetchOptions = { method, headers };

  if (payload != null && method !== 'GET') {
    if (payload instanceof FormData) {
      fetchOptions.body = payload;
      delete fetchOptions.headers['Content-Type']; // let browser set multipart boundary
    } else {
      fetchOptions.body = JSON.stringify(payload);
    }
  }

  const response = await fetch(`${base_url}${path}`, fetchOptions);
  const data = await response.json().catch(() => ({}));

  if (!response.ok) {
    const message = data.msg || data.message || data.error || `Request failed (${response.status})`;
    throw new Error(message);
  }

  return data;
};
